import { Tooltip } from 'antd';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react'
import { Area, AreaChart, ResponsiveContainer, XAxis, YAxis } from 'recharts';
import VitalService from '../../../../Services/VitalService';
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
dayjs.extend(isSameOrBefore);

function VitalGraph({ code, color, patientId }) {
    const [chartData, setChartData] = useState([]);

    useEffect(() => {
        const fetchData = async () => {
            const result = await getVitalsForGraph(code);
            setChartData(result);
        };
        fetchData();
    }, [code,patientId]);

    const CustomTooltip = ({ active, payload }) => {
        if (active && payload && payload.length) {
            const data = payload[0]?.payload;

            if (!data) return null;

            const hasDual = data.data1 != null || data.data2 != null;
            const valueText = hasDual
                ? `${data.data1 ?? "-"}${data.data2 ? ` / ${data.data2}` : ""}`
                : data.uv ?? "-";

            return (
                <div
                    style={{
                        backgroundColor: "#fff",
                        border: "1px solid #ccc",
                        padding: "8px",
                        borderRadius: "4px",
                        color: "#333",
                        fontSize: "12px",
                    }}
                >
                    <p style={{ margin: 0, fontWeight: "bold" }}>
                        {dayjs(data.date).format("dddd, YYYY-MM-DD")}
                    </p>

                    <p style={{ margin: 0, color: "#FB7185" }}>Value: {valueText}</p>
                </div>
            );
        }

        return null;
    };



    const getVitalsForGraph = async (code) => {
        const fromDate = dayjs().subtract(6, "day").startOf("day");
        const toDate = dayjs().endOf("day");

        const vitalCodes = code === "BPS" ? ["BPS", "BPD"] : [code];

        const payload = {
            patientId,
            vitalCodes,
            fromDate: fromDate.format("YYYY-MM-DD HH:mm:ss"),
            toDate: toDate.format("YYYY-MM-DD HH:mm:ss"),
        };

        const result = await VitalService.getSpecificVitalData(payload);

        const valuesByCode = {};

        (result?.data || [])
            .filter((item) => item.value && item.value !== 0 && item.value !== "0")
            .forEach((item) => {
                const key = dayjs(item.recordedDateTime).format("YYYY-MM-DD");
                const vitalCode = item.vitalCode;
                if (!valuesByCode[vitalCode]) valuesByCode[vitalCode] = {};
                valuesByCode[vitalCode][key] = item.value;
            });

        const data = [];
        let current = fromDate;
        while (current.isSameOrBefore(toDate, "day")) {
            const dateKey = current.format("YYYY-MM-DD");
            data.push({
                day: current.format("dd")[0],
                date: dateKey,
                data1: valuesByCode["BPS"]?.[dateKey] ?? null,
                data2: valuesByCode["BPD"]?.[dateKey] ?? null,
                uv: valuesByCode[code]?.[dateKey] ?? null,
            });
            current = current.add(1, "day");
        }

        return data;
    };

    const handleAreaClick = (e) => {
        if (e && e.activePayload && e.activePayload[0]) {
            const clickedData = e.activePayload[0].payload;
        }
    };

    return (
        <div className="w-full h-[100px] flex items-center justify-center">
            {chartData?.length > 0 ? (
                <ResponsiveContainer width="100%" height={100}>
                    <AreaChart data={chartData} onClick={handleAreaClick}>
                        <defs>
                            <linearGradient id="colorUv" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor={color} stopOpacity={0.8} />
                                <stop offset="95%" stopColor={color} stopOpacity={0} />
                            </linearGradient>
                        </defs>
                        <XAxis
                            dataKey="day"
                            axisLine={false}
                            tickLine={false}
                            tick={{ fill: "#374151", fontSize: 10, fontWeight: 300 }}
                        />
                        <YAxis hide />
                        <Tooltip content={<CustomTooltip />} />
                        <Area
                            type="basis"
                            dataKey="uv"
                            stroke={color}
                            strokeWidth={2}
                            fill={color}
                            fillOpacity={0.2}
                            connectNulls={true}
                            dot={(props) => {
                                if (props.payload.uv != null) {
                                    return (
                                        <circle
                                            cx={props.cx}
                                            cy={props.cy}
                                            r={4} // size of the dot
                                            fill={color} // dot color
                                            stroke="#fff" // outline
                                            strokeWidth={1}
                                        />
                                    );
                                }
                                return null;
                            }}
                        />
                        <Area
                            type="basis"
                            dataKey="data2"
                            stroke="#82ca9d"
                            strokeWidth={2}
                            fill={"#82ca9d"}
                            fillOpacity={0.3}
                            connectNulls
                            dot={(props) =>
                                props.payload.data2 != null ? (
                                    <circle
                                        cx={props.cx}
                                        cy={props.cy}
                                        r={4}
                                        fill="#82ca9d"
                                        stroke="#fff"
                                        strokeWidth={1}
                                    />
                                ) : null
                            }
                        />
                    </AreaChart>
                </ResponsiveContainer>
            ) : (
                <div className="flex items-center justify-center w-full h-full">
                    <div className="w-full h-[100px] bg-gray-200 rounded-md animate-pulse" />
                </div>
            )}
        </div>
    );
};
 

export default VitalGraph;